import { useEffect, Suspense, lazy, Component, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store';
import { authApi } from './lib/api';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';
import AdminLayout from './components/layout/AdminLayout';

// Pages
import HomePage from './pages/HomePage';
import MapPage from './pages/MapPage';
import ChatPage from './pages/ChatPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import PostPage from './pages/PostPage';
import CreatePostPage from './pages/CreatePostPage';
import NotificationsPage from './pages/NotificationsPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import OnboardingPage from './pages/OnboardingPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminModeration from './pages/admin/AdminModeration';
import AdminReports from './pages/admin/AdminReports';
import AdminUsers from './pages/admin/AdminUsers';
import AdminAds from './pages/admin/AdminAds';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import AdminPois from './pages/admin/AdminPois';

// Lazy-loaded map editor — isolated so any error doesn't crash the main app
const AdminMapEditor = lazy(() => import('./pages/admin/AdminMapEditor'));

// Error boundary for the map editor
class MapEditorErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900">
          <div className="max-w-sm w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 text-center space-y-3">
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Map Editor Error</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary w-full"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

function RootRoute() {
  const { user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/map" replace />;
  }

  return <MapPage showSearchBar={false} showLandingAuthButton />;
}

function RegisterComingSoonPage() {
  return (
    <div className="text-center space-y-3">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Registration Coming Soon</h1>
      <p className="text-sm text-slate-600 dark:text-slate-400">
        New account registration from the website will be available soon.
      </p>
    </div>
  );
}

function App() {
  const { token, setUser, setLoading } = useAuthStore();
  
  useEffect(() => {
    async function loadUser() {
      if (token) {
        try {
          const response = await authApi.me();
          setUser(response.data);
        } catch {
          setUser(null);
        }
      }
      setLoading(false);
    }
    
    loadUser();
  }, [token, setUser, setLoading]);
  
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />

      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterComingSoonPage />} />
      </Route>
      
      {/* Onboarding */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      
      {/* Main app routes */}
      <Route element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/feed" element={<HomePage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/chat/:chatId" element={<ChatPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/profile/:identifier" element={<ProfilePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/post/:id" element={<PostPage />} />
        <Route path="/create" element={<CreatePostPage />} />
      </Route>
      
      {/* Admin routes */}
      <Route path="/lockhouse" element={
        <AdminRoute>
          <AdminLayout />
        </AdminRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="moderation" element={<AdminModeration />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="ads" element={<AdminAds />} />
        <Route path="audit" element={<AdminAuditLog />} />
        <Route path="pois" element={<AdminPois />} />
      </Route>

      {/* Map Editor — full-screen, outside AdminLayout */}
      <Route path="/lockhouse/map-editor" element={
        <AdminRoute>
          <MapEditorErrorBoundary>
            <Suspense fallback={
              <div className="h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900">
                <div className="text-center">
                  <div className="animate-spin w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-slate-600 dark:text-slate-400">Loading map editor...</p>
                </div>
              </div>
            }>
              <AdminMapEditor />
            </Suspense>
          </MapEditorErrorBoundary>
        </AdminRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
